import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';

const PaymentConfirmation = () => {
  const [loading, setLoading] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [error, setError] = useState(null);
  const [registration, setRegistration] = useState(null);
  const [receiptDownloaded, setReceiptDownloaded] = useState(false);
  
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const registrationId = queryParams.get('registration_id');
  const txRef = queryParams.get('tx_ref');

  useEffect(() => {
    const verifyPayment = async () => {
      try {
        setLoading(true);
        
        if (!registrationId && !txRef) {
          throw new Error('Missing registration ID or transaction reference');
        }
        
        // If we have txRef from Chapa redirect, verify payment
        if (txRef) {
          console.log('Verifying payment with txRef:', txRef);
          try {
            // Use relative URL to avoid CORS issues
            const response = await fetch(`/api/payment/verify/${txRef}`);
            
            if (!response.ok) {
              console.error('Payment verification failed with status:', response.status);
              throw new Error(`Failed to verify payment: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Payment verification response:', data);
            setPaymentStatus(data.data.paymentStatus);
            
            // Fetch registration details
            const regResponse = await fetch(`/api/register/${data.data.verificationData.tx_ref.split('-')[1]}`);
            
            if (regResponse.ok) {
              const regData = await regResponse.json();
              setRegistration(regData.data);
            } else {
              console.error('Failed to fetch registration details:', regResponse.status);
            }
          } catch (error) {
            console.error('Error during payment verification:', error);
            throw error;
          }
        } 
        // If we only have registrationId, fetch registration details
        else if (registrationId) {
          console.log('Fetching registration details with ID:', registrationId);
          try {
            // Use relative URL to avoid CORS issues
            const response = await fetch(`/api/register/${registrationId}`);
            
            if (!response.ok) {
              console.error('Failed to fetch registration details with status:', response.status);
              throw new Error(`Failed to fetch registration details: ${response.status}`);
            }
            
            const data = await response.json();
            console.log('Registration details response:', data);
            setRegistration(data.data);
            setPaymentStatus(data.data.paymentStatus);
          } catch (error) {
            console.error('Error fetching registration details:', error);
            throw error;
          }
        }
      } catch (err) {
        setError(err.message);
        console.error('Error verifying payment:', err);
      } finally {
        setLoading(false);
      }
    };
    
    verifyPayment();
  }, [registrationId, txRef]);

  // Auto-download receipt once payment is confirmed
  useEffect(() => {
    if (paymentStatus === 'completed' && !receiptDownloaded) {
      const receiptUrl = (registration && registration.receiptUrl)
        ? registration.receiptUrl
        : `/api/register/${registrationId || (registration && registration._id)}/receipt`;

      if (receiptUrl) {
        fetch(receiptUrl)
          .then(res => {
            if (!res.ok) {
              throw new Error(`Failed to fetch receipt: ${res.status}`);
            }
            return res.blob();
          })
          .then(blob => {
            const blobUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `AMTS-Receipt-${registrationId || (registration && registration._id)}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
            setReceiptDownloaded(true);
          })
          .catch(err => console.error('Error downloading receipt:', err));
      }
    }
  }, [paymentStatus, registration, receiptDownloaded, registrationId]);

  const handleRetryPayment = async () => {
    try {
      setLoading(true);
      console.log('Retrying payment for registration ID:', registrationId);
      
      // Use relative URL to avoid CORS issues
      const response = await fetch('/api/payment/initialize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ registrationId }),
      });
      
      if (!response.ok) {
        console.error('Payment initialization failed with status:', response.status);
        throw new Error(`Failed to initialize payment: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Payment initialization response:', data);
      
      // Redirect to Chapa checkout page
      window.location.href = data.data.checkoutUrl;
    } catch (err) {
      setError(err.message);
      console.error('Error initializing payment:', err);
      setLoading(false);
    }
  };

  // Get package name
  const getPackageName = (option) => {
    return option === '3day' ? '3-Day Full Access ($1,500)' : '2-Day Access ($1,000)';
  };

  if (loading) {
    return (
      <section className="payment-confirmation" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
        <div className="payment-confirmation-container">
          <div className="loading-spinner">Verifying payment status...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="payment-confirmation" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
        <div className="payment-confirmation-container error">
          <h2>Payment Verification Error</h2>
          <p className="error-message">{error}</p>
          <div className="action-buttons">
            <Link to="/" className="home-button">Return to Home</Link>
          </div>
        </div>
      </section>
    );
  }

  return (<section className="" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>

<section className="payment-confirmation" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
      <section className="payment-confirmation-container" style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)' }}>
        {paymentStatus === 'completed' ? (
          <>
            <div className="payment-status success">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
              <h2>Payment Successful!</h2>
            </div>
            
            <div className="confirmation-details">
              <p>Thank you for registering for the Africa Maritime Conference!</p>
              <p>Your payment has been successfully processed.</p>
              
              {registration && (
                <div className="registration-summary">
                  <h3>Registration Summary</h3>
                  <div className="summary-item">
                    <span>Name:</span>
                    <span>{registration.firstName} {registration.lastName}</span>
                  </div>
                  <div className="summary-item">
                    <span>Email:</span>
                    <span>{registration.email}</span>
                  </div>
                  <div className="summary-item">
                    <span>Package:</span>
                    <span>{getPackageName(registration.paymentOption)}</span>
                  </div>
                </div>
              )}
            </div>
            
            {paymentStatus === 'completed' && (
              <div className="payment-success">
                <h3>Payment Successful!</h3>
                <p>Your registration for the Africa Maritime Talent Summit is confirmed.</p>
                <p>Registration ID: <strong>{registrationId || (registration && registration._id)}</strong></p>
                {receiptDownloaded && (
                  <p style={{ color: '#16a34a', marginTop: '0.5rem' }}>✔ Receipt PDF has been downloaded to your device.</p>
                )}
                <div className="email-confirmation-box">
                  <p><i className="fas fa-envelope"></i> A confirmation email has been sent to your registered email address.</p>
                  <p>Please check your inbox (and spam folder) for detailed information about the event.</p>
                </div>
                <Link to="/" className="btn-primary">Return to Home</Link>
              </div>
            )}
            
            <div className="action-buttons">
              <Link to="/" className="home-button">Return to Home</Link>
            </div>
          </>
        ) : paymentStatus === 'failed' ? (
          <>
            <div className="payment-status failed">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <h2>Payment Failed</h2>
            </div>
            
            <div className="confirmation-details">
              <p>We're sorry, but your payment could not be processed.</p>
              <p>This could be due to insufficient funds, incorrect card details, or other banking issues.</p>
              
              <div className="next-steps">
                <h3>What to do next</h3>
                <p>You can try again with a different payment method or contact your bank if the problem persists.</p>
              </div>
            </div>
            
            <div className="action-buttons">
              <button 
                className="retry-button" 
                onClick={handleRetryPayment}
                disabled={loading}
              >
                {loading ? 'Processing...' : 'Retry Payment'}
              </button>
              <Link to="/" className="home-button">Return to Home</Link>
            </div>
          </>
        ) : (
          <>
            <div className="payment-status pending">
              <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              <h2>Payment Pending</h2>
            </div>
            
            <div className="confirmation-details">
              <p>Your payment is currently being processed.</p>
              <p>This may take a few moments to complete.</p>
              
              <div className="next-steps">
                <h3>What to do next</h3>
                <p>Please do not close this page. You will be automatically redirected once the payment is confirmed.</p>
                <p>If you are not redirected within a few minutes, please refresh the page.</p>
              </div>
            </div>
            
            <div className="action-buttons">
              <button 
                className="retry-button" 
                onClick={() => window.location.reload()}
              >
                Refresh Status
              </button>
              <Link to="/" className="home-button">Return to Home</Link>
            </div>
          </>
        )}
      </section>
    </section>
 

  </section> );
};

export default PaymentConfirmation;
